<template>
  <v-menu
    ref="menu"
    v-model="show"
    :close-on-content-click="false"
    :return-value.sync="date"
    transition="scale-transition"
    offset-y
    min-width="290px"
    :close-on-click="false"
  >
    <template v-slot:activator="{ on, attrs }">
      <v-text-field
        class="min-w-230"
        :value="formattedDate"
        :label="label || $t('message.common.dateRange')"
        prepend-icon="icon-calendar"
        readonly
        v-bind="attrs"
        v-on="on"
      ></v-text-field>
    </template>
    <v-date-picker
      v-model="date"
      first-day-of-week="1"
      :locale="selectedLocale"
      color="primary"
      no-title
      scrollable
      range
      class="pa-2 px-4"
    >
      <div class="full-width">
        <div class="dates-select">
          <v-text-field
            v-model="dateFrom"
            :label="$t('message.common.dateFrom')"
            :disabled="!editDates"
            @keyup.enter="handleEditDatesBtn"
          ></v-text-field
          ><v-text-field
            v-model="dateTo"
            :label="$t('message.common.dateTo')"
            :disabled="!editDates"
            @keyup.enter="handleEditDatesBtn"
          ></v-text-field>
          <v-btn icon color="primary" @click="handleEditDatesBtn">
            <v-icon>
              {{ editDates ? "icon-check" : "icon-edit" }}
            </v-icon>
          </v-btn>
        </div>
        <div class="d-flex justify-space-between">
          <ui-button outlined transparent color="primary" @click="show = false"
            >{{ $t("message.common.cancel") }}
          </ui-button>
          <v-spacer></v-spacer>
          <ui-button outlined transparent color="primary" @click="handleDate"
            >{{ $t("message.common.choose") }}
          </ui-button>
        </div>
      </div>
    </v-date-picker>
  </v-menu>
</template>

<script>
import moment from "moment";
import { FORMAT } from "@/config/date-format";
import { PERIOD } from "@/enums/period.enum";
import { gtmEvent__filterDatePeriod } from "@/helpers/gtm-events";
import { mapState } from "vuex";

export default {
  name: "DateRangePicker",
  model: {
    prop: "value",
    event: "change"
  },
  props: {
    value: {
      type: Array,
      required: true
    },
    label: {
      type: String,
      default: ""
    }
  },
  data: () => ({
    show: false,
    date: null,
    dateFormatted: null,
    dateFrom: moment()
      .subtract(1, PERIOD.MONTH)
      .add(1, PERIOD.DAY)
      .format(FORMAT.DATE),
    dateTo: moment().format(FORMAT.DATE),
    editDates: false,
    dateInputType: null
  }),
  computed: {
    ...mapState({
      selectedLocale: state => state.settings.selectedLocale
    }),
    formattedDate() {
      if (this.date && this.date[0] && this.date[1]) {
        return `${moment(this.date[0]).format(FORMAT.DATE)} - ${moment(
          this.date[1]
        ).format(FORMAT.DATE)}`;
      }
      return "";
    }
  },
  watch: {
    date(newDate) {
      if (newDate[0] && this.dateFrom !== newDate[0]) {
        this.dateFrom = newDate[0];
      }
      if (newDate[1] && this.dateTo !== newDate[1]) {
        this.dateTo = newDate[1];
      }

      this.dateInputType = "calendar";
    }
  },
  created() {
    this.date = [...this.value];
  },
  methods: {
    handleDate() {
      if (this.date[1]) {
        if (moment(this.date[0]).isBefore(this.date[1])) {
          this.onChange();
        } else {
          this.date = [this.date[1], this.date[0]];
          this.onChange();
        }
      } else {
        this.date = [this.date[0], this.date[0]];
        this.onChange();
      }

      gtmEvent__filterDatePeriod({
        dateFrom: this.dateFrom,
        dateTo: this.dateTo,
        dateInputType: this.dateInputType
      });

      this.$refs.menu.save(this.date);
    },

    handleEditDatesBtn() {
      if (this.editDates) {
        this.setDates();
      }
      this.editDates = !this.editDates;
    },

    async setDates() {
      const oldDate = this.date;

      if (!moment(this.dateFrom).isValid()) {
        this.dateFrom = oldDate[0];
      }
      if (!moment(this.dateTo).isValid()) {
        this.dateTo = oldDate[1];
      }
      this.date = [this.dateFrom, this.dateTo];

      await this.$nextTick(() => {
        this.dateInputType = "manual";
      });
    },

    onChange() {
      this.$emit("change", this.date);
    }
  }
};
</script>

<style lang="scss" scoped>
.dates-select {
  display: flex;
  align-items: center;
  padding: $spacing 0;
  border-top: 1px solid $light-3;
  margin-bottom: $spacing;

  .v-text-field {
    width: 100px;
    margin-bottom: -$spacing * 2;
  }

  & > :not(:last-child) {
    margin-right: $spacing;
  }
}

.min-w-230 {
  min-width: 230px;
}
</style>
